<template>
  <div
    id="user-right-header"
    class="d-flex flex-column align-end"
  >
    <user-pagination v-if="viewUserFromOrg && orgIsSelected" />
    <div class="d-flex flex-column user-admin-btns">
      <v-btn
        color="muted"
        text
        class="clear-btn darken reset-pass-btn user-admin-btn"
        @click="onResetPassword"
      >
        Reset Password
      </v-btn>
      <div @click="checkAdmin">
        <v-btn
          v-if="removeAbility"
          text
          class="clear-btn remove-from-org user-admin-btn"
          :disabled="disableEditing"
          @click="onRemoveFromOrganization"
        >
          Remove From Organization
        </v-btn>
      </div>
    </div>
    <div v-if="globalAlertsEditable">
      <toggle
        v-model="globalReceive"
        :loading="updating"
        :disabled="updating"
        :track-color="globalReceive ? 'success' : 'error'"
        @toggle="onToggleGlobalReceive"
      >
        <template #label>
          <span class="receive-label">Receive Alerts</span>
        </template>
      </toggle>
    </div>
    <div
      v-if="!ownAccount"
      class="mt-2"
    >
      <div v-if="isInternal">
        <v-btn
          text
          color="muted"
          class="clear-btn darken user-admin-btn"
          @click="$store.dispatch('Users/impersonateUser', selectedUser.id)"
        >
          Impersonate User
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  import Toggle from '@/components/base/Toggle.vue'
  import UserPagination from '@/components/user/UserPagination.vue'

  export default {
    name: 'UserDetailsRightHeader',
    components: { Toggle, UserPagination },
    props: {
      org: Object,
      selectedUser: Object,
      globalAlertsEditable: Boolean,
    },
    data () {
      return {
        globalReceive: false,
        updating: false,
      }
    },
    computed: {
      selectedUserName () {
        return `${this.selectedUser.firstName}&nbsp;${this.selectedUser.lastName}`
      },
      ...mapGetters({
        savedGlobalReceive: 'Users/userGlobalReceiveSetting',
        isInternal: 'Permissions/isInternal',
        currentUser: 'Users/currentUser',
        canAdminUser: 'Permissions/canAdminUser',
        userRoleOnOrg: 'Permissions/userRoleOnOrg',
        isOrgOwner: 'Permissions/isOrgOwner',
      }),
      ownAccount () {
        return this.currentUser.id === this.selectedUser.id
      },
      viewUserFromOrg () {
        return this.$route.name === 'Admin User'
      },
      orgIsSelected () {
        return this.org.id !== undefined
      },
      removeAbility () {
        if (this.ownAccount) return this.orgIsSelected
        else return this.orgIsSelected && this.canAdminUser(this.selectedUser, this.org.id)
      },
      disableEditing () {
        return !this.isOrgOwner && (this.userRoleOnOrg === 'Administrator' || this.userRoleOnOrg === 'Account Owner') && !this.ownAccount && !this.isInternal
      },
    },
    watch: {
      savedGlobalReceive (newSetting, oldSetting) {
        this.initializeGlobalReceive(newSetting)
      },
    },
    mounted () { this.initializeGlobalReceive() },
    methods: {
      initializeGlobalReceive (setting = this.savedGlobalReceive) {
        this.globalReceive = setting
      },
      onToggleGlobalReceive (nextState) {
        this.updating = true
        const payload = { userId: this.selectedUser.id, updated: 'globalReceive', value: !!nextState, orgId: this.org.id || this.selectedUser.orgs[0].id }
        const onText = 'All alerts have been turned on. You will now receive all alerts from NoiseAware according to your alerts settings.'
        const offText = 'All alerts have been turned off. You will not receive any alerts from NoiseAware.'
        this.$store.dispatch('Users/updateUserNotificationSettings', payload).then(() => {
          const message = { type: nextState ? 'success' : 'warning', text: nextState ? onText : offText, unread: true }
          this.$store.dispatch('updateMessages', message, { root: true })
        })
          .catch(() => {
            const message = { type: 'error', text: `Your alerts could not be turned ${nextState ? 'on' : 'off'}. Please try again`, unread: true }
            this.$store.dispatch('updateMessages', message, { root: true })
            this.initializeGlobalReceive()
          })
          .finally(() => {
            this.updating = false
          })
      },
      onResetPassword () {
        const ownAccountText = 'Are you sure you want to reset your password? Password reset instructions will be sent to your registered email address.'
        const userText = `Are you sure you want to send <span style="font-weight: bold">${this.selectedUserName}</span> a reset password email?`
        this.$root.$confirm({
          title: 'Reset Password',
          icon: '$faKeyLight',
          text: this.ownAccount ? ownAccountText : userText,
          confirmText: 'Yes, send email',
          minHeight: 510,
          width: 400,
        }).then(() => this.$store.dispatch('Users/resetUserPassword', { email: this.selectedUser.email }))
          .catch(() => {})
      },
      checkAdmin () {
        if (this.disableEditing) {
          const accountType = (this.userRoleOnOrg === 'Account Owner') ? 'an owner account' : 'another administrator'
          this.$store.dispatch('updateMessages', { type: 'info', text: `You cannot edit ${accountType}` }, { root: true })
        }
      },
      onRemoveFromOrganization () {
        this.$root.$confirm({
          title: 'Remove User',
          icon: '$faUserSlashLight',
          text: `Are you sure you want to permanently remove <span style="font-weight: bold">${this.selectedUserName}</span> from <span style="font-weight: bold">${this.org.name}?</span>`,
          confirmText: 'Yes, remove',
          minHeight: 510,
          width: 400,
        }).then(() => this.$store.dispatch('Organizations/removeUserFromOrg', { userId: this.selectedUser.id, orgId: this.org.id }))
          .then(() => this.$router.push(`/organizations/${this.org.id}`))
          .catch(() => {})
      },

    },
  }
</script>

<style lang="scss">
#user-right-header {
  text-align: right;
  .nav-area {
    width: 130px;
    letter-spacing: 1px;
    font-weight: 600;
    font-size: 13px;
    font-family: "Lato", sans-serif;
  }
  .user-admin-btns {
    .reset-pass-btn {
      max-width:150px;
      align-self: flex-end;
      .v-btn__content {
        color: $muted;
      }
    }
  }
  .receive-label {
    color: #535353;
    padding-right: 12px;
    font-family: "Lato", sans-serif;
    font-style: normal;
    font-weight: 600;
    font-size: 14px;
    line-height: 18px;
    letter-spacing: 0.5px;
    text-transform: uppercase;
  }
  .v-label {
    margin-left: 0;
  }
  .remove-from-org {
    .v-btn__content {
      color: #{$error} !important;
    }
    &.v-btn--disabled {
      .v-btn__content {
      color: darkgrey !important;
      }
    }
  }
  .clear-btn.darken {
    color: #456E6C !important;
  }
}
.user-admin-btn {
  height: 25px !important;
  margin-bottom:5px;
  .v-btn__content {
    color: $muted;
  }
}
</style>
